import { Component } from '@theme/component';

/**
 * Brand tabs for the "Arama Merkezi" (search hub) section.
 * Implements the WAI-ARIA tabs pattern with automatic activation —
 * panels are server-rendered, so activating a tab only toggles visibility.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} tablist - The tablist wrapper element.
 * @property {HTMLButtonElement[]} tabs - The brand tab buttons.
 * @property {HTMLElement[]} panels - The brand panels, in tab order.
 * @extends {Component<Refs>}
 */
class SearchHubComponent extends Component {
  requiredRefs = ['tablist', 'tabs', 'panels'];

  #controller = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    if (window.Shopify?.designMode) {
      document.addEventListener('shopify:block:select', this.#onBlockSelect, {
        signal: this.#controller.signal,
      });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  /**
   * Activates a brand tab and reveals its panel (automatic activation).
   * Declarative `on:click="/selectTab/N"` calls pass (N, event) — the event
   * in the second slot is treated as "do focus"; only an explicit `false`
   * skips moving focus.
   * @param {number | string} index - The index of the tab to activate.
   * @param {boolean | Event} [focusOrEvent] - Pass `false` to keep focus where it is.
   */
  selectTab(index, focusOrEvent = true) {
    const activeIndex = Number(index);
    const shouldFocus = focusOrEvent !== false;

    for (const [tabIndex, tab] of this.refs.tabs.entries()) {
      const selected = tabIndex === activeIndex;
      const panel = this.refs.panels[tabIndex];

      tab.setAttribute('aria-selected', String(selected));
      tab.tabIndex = selected ? 0 : -1;

      if (panel) panel.hidden = !selected;
    }

    if (shouldFocus) this.refs.tabs[activeIndex]?.focus();
  }

  /**
   * Keyboard support for the tablist per the WAI-ARIA tabs pattern.
   * @param {KeyboardEvent} event - The keydown event.
   */
  onTablistKeydown(event) {
    const current = this.refs.tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true');
    const last = this.refs.tabs.length - 1;
    let next;

    switch (event.key) {
      case 'ArrowRight':
        next = current === last ? 0 : current + 1;
        break;
      case 'ArrowLeft':
        next = current === 0 ? last : current - 1;
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = last;
        break;
      default:
        return;
    }

    event.preventDefault();
    this.selectTab(next);
  }

  /**
   * Reveals the matching panel when a brand block is selected in the theme editor.
   * @param {Event} event - The shopify:block:select event.
   */
  #onBlockSelect = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;

    const tab = target.closest('[role="tab"]');
    if (!(tab instanceof HTMLButtonElement)) return;

    const index = this.refs.tabs.indexOf(tab);
    if (index >= 0) this.selectTab(index, false);
  };
}

if (!customElements.get('search-hub-component')) {
  customElements.define('search-hub-component', SearchHubComponent);
}

/**
 * Dropdown visibility for the hub's inline predictive search.
 *
 * The CSS could use :focus-within, but iOS Safari drops focus from the input
 * before the click on a result link completes, which would collapse the
 * dropdown mid-tap. Instead the `search-hub__search--open` class is toggled
 * here: opened when focus lands inside the search component, closed only on
 * pointer interaction outside it, focus moving elsewhere, or Escape.
 */
const SEARCH_OPEN_CLASS = 'search-hub__search--open';
const SEARCH_SELECTOR = '.search-hub__search';

/**
 * @param {Event} event - The focusin or pointerdown event.
 * @param {boolean} openWhenInside - Whether containing the target opens the dropdown.
 */
function syncOpenState(event, openWhenInside) {
  const target = event.target;
  if (!(target instanceof Node)) return;

  for (const search of document.querySelectorAll(SEARCH_SELECTOR)) {
    const inside = search.contains(target);

    if (inside && openWhenInside) {
      search.classList.add(SEARCH_OPEN_CLASS);
    } else if (!inside) {
      search.classList.remove(SEARCH_OPEN_CLASS);
    }
  }
}

document.addEventListener('focusin', (event) => syncOpenState(event, true));
document.addEventListener('pointerdown', (event) => syncOpenState(event, true));

/**
 * Popular-search chips AND brand buttons: clicking one fills the hub's search
 * input and triggers the live predictive results instead of navigating away,
 * so the visitor goes straight from a suggestion/brand to buyable products.
 * Chips are real <a> with an /search?q= href (progressive enhancement); brand
 * buttons carry data-term with the brand name.
 */
document.addEventListener('click', (event) => {
  const target = event.target;
  if (!(target instanceof Element)) return;

  const trigger = target.closest('.search-hub__chip, .search-hub__tab[data-term]');
  if (!(trigger instanceof HTMLElement)) return;

  const hub = trigger.closest('.search-hub');
  const input = hub?.querySelector('.search-input');
  if (!(input instanceof HTMLInputElement)) return;

  event.preventDefault();

  const term = (trigger.dataset.term || trigger.textContent || '').trim();
  // Brand buttons seed "Brand " (note the trailing space) so the visitor can
  // keep typing a model right after the brand; chips drop in a full query.
  const isBrand = trigger.classList.contains('search-hub__tab');
  input.value = isBrand ? term + ' ' : term;
  input.focus();
  const end = input.value.length;
  input.setSelectionRange?.(end, end);
  input.dispatchEvent(new Event('input', { bubbles: true }));
});

/**
 * Showcase state machine. The native predictive dropdown is hidden via CSS;
 * the component still morphs results into its hidden content node. We decide
 * what the showcase shows based on the input value:
 *   empty            → skeleton placeholder
 *   exactly a brand  → that brand's server-rendered best-sellers
 *   any other query  → mirrored live predictive results
 */
function setupHubResults() {
  for (const hub of document.querySelectorAll('.search-hub')) {
    if (hub.dataset.hubResultsWired === 'true') continue;

    const input = hub.querySelector('.search-input');
    const content = hub.querySelector('.predictive-search-form__content');
    const results = hub.querySelector('[data-hub-results]');
    const skeleton = hub.querySelector('[data-hub-skeleton]');
    if (!(input instanceof HTMLInputElement) || !content || !results) continue;

    hub.dataset.hubResultsWired = 'true';

    const brandWraps = Array.from(hub.querySelectorAll('[data-hub-brand]'));
    const hideBrands = () => brandWraps.forEach((w) => (w.hidden = true));

    const sync = () => {
      const query = input.value.trim();
      const brandWrap = brandWraps.find(
        (w) => (w.dataset.hubBrand || '').toLowerCase() === query.toLowerCase()
      );

      if (query === '') {
        // Idle: skeleton only
        results.hidden = true;
        results.innerHTML = '';
        hideBrands();
        if (skeleton) skeleton.hidden = false;
        return;
      }

      if (brandWrap) {
        // Brand picked (and nothing typed beyond it): show its best-sellers
        if (skeleton) skeleton.hidden = true;
        results.hidden = true;
        results.innerHTML = '';
        brandWraps.forEach((w) => (w.hidden = w !== brandWrap));
        return;
      }

      // Real query: rebuild a SINGLE clean list from the predictive content.
      // During a morph the content can briefly hold two product lists (e.g. the
      // "recently viewed" set plus the live results); copying raw HTML then made
      // two rows, each with its own "closest match" first child. Instead we take
      // the first list's first 4 cards and assemble one fresh <ul> — guaranteeing
      // a single row with exactly one closest-match tile, with no flicker.
      hideBrands();

      const staging = document.createElement('div');
      staging.innerHTML = content.innerHTML;

      const sourceList = staging.querySelector('.predictive-search-results__wrapper-products');
      const cards = sourceList
        ? Array.from(sourceList.querySelectorAll('.predictive-search-results__card--product')).slice(0, 4)
        : [];

      if (cards.length > 0) {
        const list = document.createElement('ul');
        list.className = 'predictive-search-results__list predictive-search-results__wrapper-products list-unstyled';
        cards.forEach((card) => list.appendChild(card));
        results.replaceChildren(list);
        results.hidden = false;
        if (skeleton) skeleton.hidden = true;
      } else {
        const noResults = staging.querySelector('.predictive-search-results__no-results');
        if (noResults) {
          results.replaceChildren(noResults);
          results.hidden = false;
          if (skeleton) skeleton.hidden = true;
        } else {
          // Still loading — keep the skeleton placeholder
          results.replaceChildren();
          results.hidden = true;
          if (skeleton) skeleton.hidden = false;
        }
      }
    };

    new MutationObserver(sync).observe(content, { childList: true, subtree: true });
    input.addEventListener('input', sync);
    input.addEventListener('search', sync);
    sync();
  }
}

if (document.readyState !== 'loading') {
  setupHubResults();
} else {
  document.addEventListener('DOMContentLoaded', setupHubResults);
}
document.addEventListener('keydown', (event) => {
  if (event.key !== 'Escape') return;

  for (const search of document.querySelectorAll(SEARCH_SELECTOR)) {
    search.classList.remove(SEARCH_OPEN_CLASS);
  }
});

/**
 * The stock predictive component only requests Shopify's default search
 * fields, which exclude tags — but this store encodes printer-model
 * compatibility as tags so product titles can stay short. Widening the
 * suggest request to `title,tag` keeps live results working for queries
 * like "DeskJet 2720". Only /search/suggest requests with a query are
 * touched; everything else passes through untouched.
 */
const PREDICTIVE_FIELDS = 'title,tag';
const originalFetch = window.fetch;

window.fetch = function (input, init) {
  try {
    const target = typeof input === 'string' || input instanceof URL ? input.toString() : input.url;
    const url = new URL(target, location.origin);
    const predictivePath = window.Theme?.routes?.predictive_search_url;

    if (predictivePath && url.pathname === predictivePath && url.searchParams.get('q')) {
      url.searchParams.set('resources[options][fields]', PREDICTIVE_FIELDS);
      input = typeof input === 'string' || input instanceof URL ? url.toString() : new Request(url, input);
    }
  } catch {
    // A URL parse failure must never break unrelated fetches.
  }

  return originalFetch.call(this, input, init);
};
